import styled from "@emotion/styled";
import { useFormContext } from "react-hook-form";
import { ButtonClickAnalyticsType, FieldTypeOption } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { Schedule } from "./../schedule/Schedule";
import { RadioGroup } from "../RadioGroup";
import { useEffect, useRef } from "react";

export interface SchedulingRadioGroupProps {
    /**
     * Main label for the scheduling section
     */
    label: string;

    /**
     * Optional description text explaining the scheduling options
     */
    description?: string;

    /**
     * Label for the "false" radio option (e.g., "Always")
     */
    falseLabel: string;

    /**
     * Label for the "true" radio option (e.g., "Specific time frame")
     */
    trueLabel: string;

    /**
     * Field name for the radio group in the form
     */
    fieldName: string;

    /**
     * List of available timezones
     */
    timezones: FieldTypeOption[];

    /**
     * Optional field prefix for Schedule component fields (startTimeDate, endTimeDate, etc.)
     * Use this when you have multiple SchedulingRadioGroup instances in the same form
     * to prevent field name collisions
     */
    scheduleFieldPrefix?: string;

    /**
     * Optional function to generate analytics label when radio button is clicked
     * @param isScheduled - true if "Specific time frame" is selected, false if "Always" is selected
     * @returns The analytics label to send
     * @example
     * getAnalyticsLabel={(isScheduled) => `Toggle Publishing Schedule - ${isScheduled ? "Schedule" : "Always"}`}
     */
    getAnalyticsLabel?: (isScheduled: boolean) => string;

    /**
     * Value to use for the "false" option (e.g., always/not scheduled)
     * @default "false"
     * @example "always" or "false"
     */
    falseValue?: string;

    /**
     * Value to use for the "true" option (e.g., scheduled/specific time frame)
     * @default "true"
     * @example "scheduled" or "true"
     */
    trueValue?: string;

    /**
     * if true, skips the 24-hour max duration validation on the date/time pickers
     * @default false
     */
    skipMaxDurationValidation?: boolean;
}

const StyledWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
}));

/**
 * SchedulingRadioGroup component provides a radio button group with conditional date/time/timezone inputs.
 * When "true" option is selected, displays date/time pickers and timezone selection.
 */
export const SchedulingRadioGroup = ({
    label,
    description,
    falseLabel,
    trueLabel,
    fieldName,
    timezones,
    scheduleFieldPrefix,
    getAnalyticsLabel,
    falseValue = "false",
    trueValue = "true",
    skipMaxDurationValidation = false,
}: SchedulingRadioGroupProps) => {
    const { watch } = useFormContext();
    const selectedValue = watch(fieldName);
    const sendButtonAnalytics = useButtonAnalytics();

    // track form field data changes and send analytics
    const prevValue = useRef(selectedValue);
    useEffect(() => {
        if (prevValue.current !== selectedValue) {
            // Field value has changed
            if (getAnalyticsLabel) {
                const isScheduled = selectedValue === trueValue;
                const analyticsLabel = getAnalyticsLabel(isScheduled);
                sendButtonAnalytics(analyticsLabel, ButtonClickAnalyticsType.TOGGLE);
            }
            prevValue.current = selectedValue;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedValue]);

    const showScheduleInputs = selectedValue === trueValue;

    const radioOptions = [
        { label: falseLabel, value: falseValue },
        { label: trueLabel, value: trueValue },
    ];

    return (
        <StyledWrapper>
            <RadioGroup
                inline={true}
                label={label}
                id={fieldName}
                options={radioOptions}
                variant={"horizontal"}
                description={description}
            />
            {showScheduleInputs && (
                <Schedule
                    timezones={timezones}
                    fieldPrefix={scheduleFieldPrefix}
                    skipMaxDurationValidation={skipMaxDurationValidation}
                    aria-describedby={`${fieldName}-label`}
                />
            )}
        </StyledWrapper>
    );
};

export default SchedulingRadioGroup;
